/**
 * Feature Flags
 *
 * Centralized feature flag management for controlling experimental features
 * and gradual rollouts.
 */

/**
 * Feature flag keys
 */
export enum FeatureFlag {
  /**
   * Use OSMD Phaser backend for music rendering instead of pre-rendered SVGs.
   * When enabled:
   * - MusicXML is rendered directly using OSMD's Phaser backend
   * - No SVG or JSON data is fetched from the backend
   * - NoteContainers are used for interactive note elements
   */
  USE_OSMD_PHASER_BACKEND = 'USE_OSMD_PHASER_BACKEND',
  USE_LESSON_ZOOM = 'USE_LESSON_ZOOM',
  USE_ERROR_FEEDBACK = 'USE_ERROR_FEEDBACK',
  USE_ADAPTIVE_TEMPO = 'USE_ADAPTIVE_TEMPO',
  USE_MOBILE_SIGHT_READING = 'USE_MOBILE_SIGHT_READING',
  USE_LOOPING = 'USE_LOOPING',
}

/**
 * Read a boolean from Vite env, returning the fallback when the var is absent.
 */
function envBoolean(key: string, fallback: boolean): boolean {
  const value = import.meta.env[key];
  if (value === undefined || value === '') return fallback;
  return value === 'true' || value === '1';
}

/**
 * Default values for feature flags
 */
const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  [FeatureFlag.USE_OSMD_PHASER_BACKEND]: true, // Enabled — OSMD Phaser backend active for lessons and repertoire
  [FeatureFlag.USE_LESSON_ZOOM]: envBoolean('REACT_APP_USE_LESSON_ZOOM', true),
  [FeatureFlag.USE_ERROR_FEEDBACK]: false,
  [FeatureFlag.USE_ADAPTIVE_TEMPO]: false,
  [FeatureFlag.USE_MOBILE_SIGHT_READING]: false,
  [FeatureFlag.USE_LOOPING]: false,
};

/**
 * Local storage key for feature flags
 */
const STORAGE_KEY = 'museflow_feature_flags';

/**
 * Get all feature flags from local storage
 */
function getStoredFlags(): Partial<Record<FeatureFlag, boolean>> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to read feature flags from local storage:', error);
  }
  return {};
}

/**
 * Save feature flags to local storage
 */
function saveFlags(flags: Partial<Record<FeatureFlag, boolean>>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch (error) {
    console.warn('Failed to save feature flags to local storage:', error);
  }
}

/**
 * Check if a feature flag is enabled
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  // Check URL parameter first (for testing)
  const urlParams = new URLSearchParams(window.location.search);
  const urlValue = urlParams.get(flag);
  if (urlValue !== null) {
    return urlValue === 'true' || urlValue === '1';
  }

  // Check local storage
  const storedFlags = getStoredFlags();
  if (flag in storedFlags) {
    return storedFlags[flag]!;
  }

  // Return default value
  return DEFAULT_FLAGS[flag];
}

/**
 * Set a feature flag value
 */
export function setFeatureFlag(flag: FeatureFlag, enabled: boolean): void {
  const storedFlags = getStoredFlags();
  storedFlags[flag] = enabled;
  saveFlags(storedFlags);
}

/**
 * Reset a feature flag to its default value
 */
export function resetFeatureFlag(flag: FeatureFlag): void {
  const storedFlags = getStoredFlags();
  delete storedFlags[flag];
  saveFlags(storedFlags);
}

/**
 * Reset all feature flags to their default values
 */
export function resetAllFeatureFlags(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to reset feature flags:', error);
  }
}

/**
 * Get all current feature flag values
 */
export function getAllFeatureFlags(): Record<FeatureFlag, boolean> {
  const result: Record<FeatureFlag, boolean> = { ...DEFAULT_FLAGS };
  const storedFlags = getStoredFlags();

  for (const flag of Object.values(FeatureFlag)) {
    if (flag in storedFlags) {
      result[flag] = storedFlags[flag]!;
    }
  }

  return result;
}

/**
 * Hook for using feature flags in React components
 * Note: This is a simple implementation. For real-time updates,
 * consider using a context provider or state management solution.
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  return isFeatureEnabled(flag);
}

/**
 * Convenience function to check if OSMD Phaser backend should be used
 */
export function useOSMDPhaserBackend(): boolean {
  return isFeatureEnabled(FeatureFlag.USE_OSMD_PHASER_BACKEND);
}
